import { toCalendarDate } from './certDefaultDate';
import { ProjectInspection } from './api/projectInspection';

/**
 * SW2-2239 — "which findings hold up certification", normalised across the two shapes the app carries.
 *
 * The Certification tab and the bulk/widget surfaces disagreed on more than ordering: they used
 * different *semantics*.
 *
 *   - `InspectionCertificationContext` asked whether any ACTUAL finding on THIS inspection is required
 *     for cert.
 *   - `BulkCertifyModal` asked whether the TEMPLATE declares any required type, never looking at the
 *     inspection's findings — so a template with a required type but an inspection with none returned
 *     true there and false on the tab.
 *
 * The tab's semantic is the correct one and is what this module implements: if the inspection has no
 * required findings, there is nothing to wait for. That also removes `findingTypesApi.fetchAll()` from
 * the bulk modal, which fetched every finding type in the tenant on each open.
 *
 * The join in `selectRequiredForCertFindingsFromRow` mirrors `findingCount.ts:33-40`, which already
 * normalises exactly this relationship — deliberately, so there is one shape of this logic rather than a
 * sixth variant.
 */

export type CompletedRequiredFinding = {
  finding_type_id?: number | null;
  date_completed: string | Date;
};

/**
 * Certification tab shape: `is_required_for_cert` is flattened onto each finding by
 * `utils/api/finding.ts` when the inspection's findings are loaded.
 */
export function selectRequiredForCertFindings(
  findings: any[] | null | undefined,
): CompletedRequiredFinding[] {
  if (!findings?.length) return [];

  return findings.filter(
    (finding: any) => !!finding?.is_required_for_cert && !!finding?.date_completed,
  );
}

/**
 * Bulk / widget row shape: `findings` plus a separate `findingDetails`, requiring a join on
 * `finding_type_id` AND the row's own `inspection_template`.
 *
 * Takes the MATCHED inspection row. The previous inline version read
 * `inspections[0].inspection_template` — the first inspection in the selection rather than the one being
 * resolved — which leaked one inspection's template across a multi-select bulk certify.
 */
export function selectRequiredForCertFindingsFromRow(
  row: ProjectInspection | null | undefined,
): CompletedRequiredFinding[] {
  const findings: any[] = (row as any)?.findings ?? [];
  const findingDetails: any[] = (row as any)?.findingDetails ?? [];
  const template: any = (row as any)?.inspection_template;

  if (!findings.length || !template) return [];

  const disableCarryOvers = (row as any)?.project?.client?.disable_carry_overs;

  return findings.filter((finding: any) => {
    if (!finding?.date_completed) return false;

    // Mirrors findingCount.ts and the backend's own required-findings query, both of which exclude
    // carried-over findings for clients with the setting on.
    if (disableCarryOvers && finding?.InspectionFindingModel?.carried_over) {
      return false;
    }

    return (
      findingDetails.find(
        (detail: any) =>
          detail.finding_type_id === finding.finding_type_id &&
          detail.inspection_template_id === template.id &&
          detail.is_required_for_cert,
      ) !== undefined
    );
  });
}

/**
 * The latest required-for-cert completion, as a calendar date in the project's timezone.
 *
 * Returns null when nothing qualifies — which is what makes the old
 * `isFindingRequired() && latestFindingClosureDate()` conjunction unnecessary: a non-null result
 * already implies a required, completed finding exists. One input replaces both.
 */
export function getRequiredFindingCompletion(
  findings: CompletedRequiredFinding[],
  timezone: string,
): Date | null {
  const days = findings
    .map(finding => toCalendarDate(finding.date_completed, timezone))
    .filter((d): d is Date => !!d);

  if (!days.length) return null;

  return days.reduce((latest, day) =>
    day.getTime() > latest.getTime() ? day : latest,
  );
}
